import type { StraightenerParameterConfig } from "./repositories/StraightenerParamLimitsRepository";

// Pure limit-resolution helpers over the backend parameter config rows
// (fetch lives in repositories/StraightenerParamLimitsRepository, the query in
// hooks/useStraightenerParamLimits).

export interface ParamLimit {
	min: number | null;
	max: number | null;
}

const NO_LIMIT: ParamLimit = { min: null, max: null };

export type LimitOverrides = Map<string, ParamLimit>;

// Build the code -> limit map from the backend catalog rows.
export const buildLimitOverrides = (
	rows?: StraightenerParameterConfig[],
): LimitOverrides => {
	const map: LimitOverrides = new Map();
	for (const r of rows ?? [])
		map.set(r.Code, { min: r.MinValue, max: r.MaxValue });
	return map;
};

// ---- Resolution + checks --------------------------------------------------

// Effective limit for a parameter (no limit until the catalog loads).
export const resolveLimit = (
	code: string,
	overrides?: LimitOverrides,
): ParamLimit => overrides?.get(code) ?? NO_LIMIT;

// True when `value` violates the parameter's limits.
export const isOutOfLimit = (
	code: string,
	value: number | null,
	overrides?: LimitOverrides,
): boolean => {
	if (value == null) return false;
	const { min, max } = resolveLimit(code, overrides);
	return (min != null && value < min) || (max != null && value > max);
};

// Human label for the allowed range, e.g. "≥ 0", "0–100", "≤ 100".
export const limitRangeLabel = (
	code: string,
	overrides?: LimitOverrides,
): string | null => {
	const { min, max } = resolveLimit(code, overrides);
	if (min != null && max != null) return `${min}–${max}`;
	if (min != null) return `≥ ${min}`;
	if (max != null) return `≤ ${max}`;
	return null;
};
